/**
 * @module components/ThankYouPostcard
 * @description The post-Gala thank-you postcard, front and back, from
 * Samantha's layout (_sources/AFJ Post Gala Thank you Postcard Front.png and
 * AFJ Postcard Back.png). Same 3:2 container-unit system as the story cards,
 * so it prints, exports and previews through the same engine.
 *
 * Back, per the mock-up notes (_sources/WhatsApp … 18.44.20): the child's
 * note is printed (a scan of the real note when we have one), and the
 * host's line and the address stay blank to be written by hand after
 * printing. Typed values fill them only when admin enters them.
 */
import { forwardRef } from "react";
import type { ThankYouCard } from "../domain/types";
import { CAMPAIGN } from "../config/campaign";
import { THANK_YOU_PHOTO_SHARE } from "../design/cardLayout";
import { assetUrl } from "../lib/assetUrl";
import { BrandLockup } from "./Brand";
import { CardBody, CardHeader, ContentHoldNotice, orientationClass } from "./StoryPostcard";
import { useCardOrientation } from "../app/cardOrientation";
import { MailingPanel } from "./CardBacks";

const FROM_JAMAICA = "From Jamaica, With Love.";

export interface ThankYouFaceProps {
    card: ThankYouCard;
    className?: string;
}

export const ThankYouFront = forwardRef<HTMLElement, ThankYouFaceProps>(function ThankYouFront({ card, className }, ref) {
    const orientation = useCardOrientation();
    return (
        <article ref={ref} className={`postcard ty ${orientationClass(orientation)} ${className ?? ""}`} aria-label={`Thank-you postcard: ${card.place}`}>
            {/* The thank-you card speaks about the Gala, so it keeps the Gala postmark in either edition (Samantha's design). */}
            <CardHeader kicker="Postcard from Jamaica" title={card.place} postmark="gala" />
            <CardBody
                orientation={orientation}
                share={THANK_YOU_PHOTO_SHARE}
                className="ty-body"
                photo={
                    <figure className="pc-photo ty-photo">
                        <img src={assetUrl(card.image.src)} alt={card.image.alt} />
                        {card.image.isIllustrative && <figcaption className="pc-badge">Illustrative image</figcaption>}
                    </figure>
                }
                story={
                    <div className="ty-story">
                        <p className="ty-headline">{card.headline}</p>
                        <p className="ty-subhead">{card.subhead}</p>
                        <p className="ty-copy">{card.body}</p>
                        <hr className="pc-rule ty-rule" />
                        <p className="ty-turn">{card.turnOverLine}</p>
                        {card.contentHold && <ContentHoldNotice />}
                    </div>
                }
            />
            <footer className="pc-foot">
                <div>
                    <p className="pc-love">This is your love at work.</p>
                    <p className="pc-sub">{CAMPAIGN.organizationName} · {CAMPAIGN.siteLabel}</p>
                </div>
                <p className="pc-script">{FROM_JAMAICA}</p>
            </footer>
        </article>
    );
});

/** The child's note: the scan when we have one, otherwise the typed note on ruled paper. */
function NoteCard({ card }: { card: ThankYouCard }) {
    const { note } = card;
    return (
        <figure className="ty-note">
            {note.scanSrc ? (
                <img className="ty-note-scan" src={assetUrl(note.scanSrc)} alt={`Handwritten note: ${note.text}`} />
            ) : (
                <div className="ty-note-paper hand-child">
                    {note.text.split("\n").map((line, i) => (
                        <p key={i}>{line}</p>
                    ))}
                </div>
            )}
            <figcaption>{note.caption}</figcaption>
        </figure>
    );
}

export const ThankYouBack = forwardRef<HTMLElement, ThankYouFaceProps>(function ThankYouBack({ card, className }, ref) {
    const orientation = useCardOrientation();
    const personal = card.personalNote.split("\n").filter((l) => l.trim());
    return (
        <article ref={ref} className={`postcard postcard--back tyb ${orientationClass(orientation)} ${className ?? ""}`} aria-label={`Thank-you postcard back: ${card.place}`}>
            <div className="tyb-message">
                <p className="pc-kicker tyb-kicker">{card.backKicker}</p>
                <NoteCard card={card} />
                <p className="tyb-copy">
                    {card.message} <em>{card.closingLine}</em>
                </p>
                {/* Host's handwritten line: typed preview if entered, otherwise two blank ruled lines. */}
                <div className={`tyb-personal ${personal.length ? "hand-ink" : "tyb-personal--blank"}`}>
                    {(personal.length ? personal : ["", ""]).map((line, i) => (
                        <p key={i}>{line}</p>
                    ))}
                </div>
                <div className="mb-sign tyb-sign">
                    <BrandLockup variant="horizontal" className="mb-lockup" />
                    <p className="cb-legal">{CAMPAIGN.organizationName} · {CAMPAIGN.siteLabel}</p>
                </div>
                {card.contentHold && <ContentHoldNotice />}
            </div>
            <span className="mb-divider" aria-hidden="true" />
            <MailingPanel script="From Jamaica, With Love" recipient={card.recipient} />
        </article>
    );
});
